import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Paper,
  TableContainer,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Add, ExpandMore, Remove } from "@mui/icons-material";
import axios from "axios";

const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function AddUnitsWizard({ open, onClose }) {
  const { t } = useTranslation();
  const steps = [
    t("add_units_wizard.step_select_type"),
    t("add_units_wizard.step_enter_structure"),
    t("add_units_wizard.step_preview"),
  ];
  const [activeStep, setActiveStep] = useState(0);
  const [buildingType, setBuildingType] = useState("Tower");
  const [count, setCount] = useState(1); // Number of buildings
  const [floorsBlocks, setFloorsBlocks] = useState(1); // Floors or Blocks per building
  const [unitsPerFloorBlock, setUnitsPerFloorBlock] = useState(1); // Units per floor/block
  const [units, setUnits] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [showFailedDetails, setShowFailedDetails] = useState(false);

  const [nameTemplate, setNameTemplate] = useState(
    "{{label}}{{floor}}{{unit}}"
  );

  const handleNext = () => {
    if (activeStep === 1) {
      generateUnits();
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (activeStep === 1) {
      setCount(1);
      setFloorsBlocks(1);
      setUnitsPerFloorBlock(1);
    }
    if (activeStep === 2) {
      setUnits([]);
      setNameTemplate("Flat {{floor}}{{unit}} {{buildingName}}");
    }
    setActiveStep((prev) => prev - 1);
  };

  const generateUnits = () => {
    const generated = [];

    for (let i = 0; i < count; i++) {
      const buildingLabel = alphabet[i] || `B${i + 1}`;
      const buildingName =
        (buildingType === "Tower" ? "Tower " : "Villa ") + buildingLabel;

      for (let j = 1; j <= floorsBlocks; j++) {
        for (let k = 1; k <= unitsPerFloorBlock; k++) {
          const unitName = `${buildingLabel}${j}${k
            .toString()
            .padStart(2, "0")}`;
          // const unitName = nameTemplate
          //   .replace("{{buildingName}}", buildingName)
          //   .replace("{{floor}}", String(j))
          //   .replace("{{block}}", String(j).padStart(2, "0"))
          //   .replace("{{unit}}", String(k).padStart(2, "0"));

          generated.push({
            buildingName, // e.g. "Tower A" or "Villa A"
            buildingType,
            floor: j,
            flat_number: k,
            unitName,
          });
        }
      }
    }

    setUnits(generated);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/compound/units/createbulk`,
        { units }
      );
      console.log("Units created:", response.data);
      setSubmitResult(response.data);
    } catch (error) {
      console.error("Error creating units:", error);
      alert("Something went wrong.");
    } finally {
      setSubmitting(false);
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setBuildingType("Tower");
    setCount(1);
    setFloorsBlocks(1);
    setUnitsPerFloorBlock(1);
    setUnits([]);
    setSubmitResult(null);
    setShowFailedDetails(false);
    setNameTemplate("{{label}}{{floor}}{{unit}}");
    onClose();
  };

  // Group units by buildingName for display
  const groupedUnits = units.reduce((acc, unit) => {
    const group = acc.find((g) => g.buildingName === unit.buildingName);
    if (group) {
      group.units.push(unit);
    } else {
      acc.push({ buildingName: unit.buildingName, units: [unit] });
    }
    return acc;
  }, []);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        {t("add_units_wizard.title")}
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {submitResult ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent={submitResult.failed > 0 ? "flex-start" : "center"}
            py={submitResult.failed > 0 ? 4 : 8}
            px={3}
            sx={{
              bgcolor: "#2a2d34",
              borderRadius: 2,
              color: "#f0f0f0",
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              py={4}
              px={3}
            >
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {t("add_units_wizard.submission_result")}
              </Typography>

              {/* Summary Row */}
              <Box
                display="flex"
                gap={4}
                mt={2}
                mb={3}
                justifyContent="center"
                flexWrap="wrap"
              >
                <Box textAlign="center">
                  <Typography variant="subtitle1" color="gray">
                    {t("add_units_wizard.total_units")}
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {submitResult.total}
                  </Typography>
                </Box>

                <Box textAlign="center">
                  <Typography variant="subtitle1" color="gray">
                    {t("add_units_wizard.created")}
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="lightgreen">
                    {submitResult.created}
                  </Typography>
                </Box>

                <Box textAlign="center">
                  <Typography variant="subtitle1" color="gray">
                    {t("add_units_wizard.failed")}
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="#f87171">
                    {submitResult.failed}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Show Failed Details */}
            {submitResult.failed > 0 && (
              <Box width="100%">
                <Box display="flex" justifyContent="center">
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setShowFailedDetails((prev) => !prev)}
                    sx={{ mb: 2 }}
                  >
                    {showFailedDetails
                      ? t("add_units_wizard.hide_failed")
                      : t("add_units_wizard.show_failed")}
                  </Button>
                </Box>

                {showFailedDetails && (
                  <Paper
                    sx={{
                      mt: 2,
                      width: "100%",
                    }}
                  >
                    <TableContainer
                      component={Box}
                      sx={{
                        maxHeight: "400px",
                        overflow: "auto", // enable both horizontal and vertical
                        width: "100%", // ensure it spans available space
                        scrollbarWidth: "thin",
                        "&::-webkit-scrollbar": {
                          height: "8px", // 👈 ensures horizontal bar shows
                          width: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#f0f0f0",
                          borderRadius: "8px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#c1c1c1",
                          borderRadius: "8px",
                          border: "2px solid #f0f0f0",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                          backgroundColor: "#a0a0a0",
                        },
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: "#aaa" }}>#</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>
                              {t("add_units_wizard.col_building")}
                            </TableCell>
                            <TableCell sx={{ color: "#aaa" }}>{t("add_units_wizard.col_floor")}</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>{t("add_units_wizard.col_flat")}</TableCell>
                            <TableCell sx={{ color: "#aaa" }}>{t("add_units_wizard.col_reason")}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {submitResult.failedDetails.map((item, i) => (
                            <TableRow key={i}>
                              <TableCell>{i + 1}</TableCell>
                              <TableCell>{item.buildingName}</TableCell>
                              <TableCell>{item.floor}</TableCell>
                              <TableCell>{item.flat_number}</TableCell>
                              <TableCell>{item.reason}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}
              </Box>
            )}

            <Box mt="auto" alignSelf="center">
              <Button
                variant="contained"
                onClick={handleClose}
                sx={{
                  borderRadius: 2,
                  backgroundColor: "#4ea8de",
                  "&:hover": { backgroundColor: "#3a90c6" },
                  px: 4,
                  py: 1,
                  mt: 1,
                  fontWeight: 500,
                }}
              >
                {t("add_units_wizard.close")}
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel
                    sx={{
                      "& .MuiStepIcon-root": {
                        color: "#bdbdbd",
                      },
                      "& .MuiStepIcon-root.Mui-completed": {
                        color: "#00b4d8",
                      },
                      "& .MuiStepIcon-root.Mui-active": {
                        color: "#4caf50",
                      },
                      "& .MuiStepLabel-label": {
                        color: "#bdbdbd",
                      },
                      "& .MuiStepLabel-label.Mui-active": {
                        color: "#4caf50",
                        fontWeight: "bold",
                      },
                      "& .MuiStepLabel-label.Mui-completed": {
                        color: "#00b4d8",
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Step 1: Select Type */}
            {activeStep === 0 && (
              <Box display="flex" justifyContent="center">
                <FormControl>
                  <Typography variant="h5" gutterBottom>
                    {t("add_units_wizard.type_question")}
                  </Typography>
                  <RadioGroup
                    value={buildingType}
                    onChange={(e) => setBuildingType(e.target.value)}
                  >
                    <FormControlLabel
                      value="Tower"
                      control={
                        <Radio
                          sx={{
                            "&.Mui-checked": {
                              color: "#5089FF",
                            },
                          }}
                        />
                      }
                      label={t("add_units_wizard.towers")}
                    />
                    <FormControlLabel
                      value="Villa"
                      control={
                        <Radio
                          sx={{
                            "&.Mui-checked": {
                              color: "#5089FF",
                            },
                          }}
                        />
                      }
                      label={t("add_units_wizard.villas")}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            )}

            {/* Step 2: Enter Structure */}
            {activeStep === 1 && (
              <Box>
                {[
                  {
                    label: buildingType === "Tower"
                      ? t("add_units_wizard.how_many_towers")
                      : t("add_units_wizard.how_many_areas"),
                    value: count,
                    setter: setCount,
                  },
                  {
                    label: buildingType === "Tower"
                      ? t("add_units_wizard.how_many_floors")
                      : t("add_units_wizard.how_many_blocks"),
                    value: floorsBlocks,
                    setter: setFloorsBlocks,
                  },
                  {
                    label: buildingType === "Tower"
                      ? t("add_units_wizard.how_many_flats")
                      : t("add_units_wizard.how_many_villas_block"),
                    value: unitsPerFloorBlock,
                    setter: setUnitsPerFloorBlock,
                  },
                ].map((field, idx) => (
                  <Box
                    key={idx}
                    display="flex"
                    alignItems="center"
                    gap={2}
                    mb={3}
                  >
                    <Typography variant="h5" sx={{ width: 400 }}>
                      {field.label}
                    </Typography>
                    <IconButton
                      onClick={() => field.setter(Math.max(1, field.value - 1))}
                      disabled={field.value <= 1}
                    >
                      <Remove />
                    </IconButton>
                    <TextField
                      type="number"
                      size="small"
                      value={field.value}
                      onChange={(e) =>
                        field.setter(Math.max(1, Number(e.target.value) || 1))
                      }
                      inputProps={{
                        style: { width: 50, textAlign: "center" },
                        min: 1,
                      }}
                    />
                    <IconButton onClick={() => field.setter(field.value + 1)}>
                      <Add />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {/* Step 3: Preview and Rename */}
            {activeStep === 2 && (
              <Box>
                <Box mb={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    {t("add_units_wizard.name_template_label")}
                  </Typography>
                  <TextField
                    fullWidth
                    value={nameTemplate}
                    onChange={(e) => {
                      setNameTemplate(e.target.value);
                      generateUnits();
                    }}
                    placeholder="e.g., {{label}}{{floor}}{{unit}}"
                    helperText={t("add_units_wizard.name_template_helper")}
                  />
                  <Button
                    sx={{ mt: 1 }}
                    onClick={() => {
                      const newUnits = units.map((u) => ({
                        ...u,
                        unitName: nameTemplate
                          .replace("{{label}}", u.label)
                          .replace("{{floor}}", u.floor.toString())
                          .replace(
                            "{{unit}}",
                            u.flat_number.toString().padStart(2, "0")
                          ),
                      }));
                      setUnits(newUnits);
                    }}
                  >
                    {t("add_units_wizard.apply_template")}
                  </Button>
                </Box>

                <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                  {groupedUnits.map(({ buildingName, units: bUnits }) => (
                    <Accordion key={buildingName} defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography fontWeight="bold">
                          {buildingName}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {bUnits.map((u, i) => {
                          const unitIndex = units.findIndex(
                            (item) => item === u
                          );
                          return (
                            <Box
                              key={unitIndex}
                              display="flex"
                              alignItems="center"
                              mb={1}
                              gap={1}
                            >
                              <Typography sx={{ width: 30 }}>
                                {unitIndex + 1}
                              </Typography>
                              <TextField
                                size="small"
                                fullWidth
                                value={u.unitName}
                                onChange={(e) => {
                                  const updated = [...units];
                                  updated[unitIndex].unitName = e.target.value;
                                  setUnits(updated);
                                }}
                              />
                              <Button
                                size="small"
                                sx={{ ml: 1 }}
                                onClick={() => {
                                  const updated = units.map((unit) =>
                                    unit.buildingName === u.buildingName
                                      ? { ...unit, unitName: u.unitName }
                                      : unit
                                  );
                                  setUnits(updated);
                                }}
                              >
                                {buildingType === "Tower"
                                  ? t("add_units_wizard.apply_to_tower")
                                  : t("add_units_wizard.apply_to_villa")}
                              </Button>
                            </Box>
                          );
                        })}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </Box>
            )}

            <Box mt={4} display="flex" justifyContent="space-between">
              <Button disabled={activeStep === 0} onClick={handleBack}>
                {t("add_units_wizard.back")}
              </Button>
              {activeStep < steps.length - 1 ? (
                <Button variant="contained" onClick={handleNext}>
                  {t("add_units_wizard.next")}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleSubmit}
                  sx={{ color: "#fff" }}
                  disabled={submitting}
                >
                  {submitting ? t("add_units_wizard.submitting") : t("add_units_wizard.submit")}
                </Button>
              )}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
